import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { CourseEditBasic } from "@shared/models/analytics/course"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course } from "@shared/models/course"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { partial } from "lodash-es"
import Patch from "@shared/models/patch-updater"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultCourseActivity from "./default-course-activity"
import defaultCourseContext from "./default-course-context"
import defaultCourseModels from "./default-course-models"

interface Payload {
  courseId: string
  planbookId: string
  courseDateTimeSetting: Course["attributes"]["courseDateTimeSetting"]
}

export default function prepareCourseSetEnterTimesStateAction(payload: Payload): PreparedAction {
  return {
    name: "COURSE_SET_ENTER_TIMES_STATE",
    payload: payload,
    patches: patches(payload),
    undoPatches: null,
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload) {
  let patch = Patch.create("course", payload.courseId).set(
    "attributes.courseDateTimeSetting",
    payload.courseDateTimeSetting
  ).patch
  return [patch]
}

const stateDescriptions: Record<Course["attributes"]["courseDateTimeSetting"], string> = {
  UNSET: "undecided times",
  NO: "not have times",
  YES_UNSET: "have times",
  YES_SAME: "have the same times every day",
  YES_DIFFERENT: "have different times",
}

function narrative(payload: Payload, findFn: FindFn, userId): Narrative {
  let models = defaultCourseModels(payload, findFn, userId)
  let courseTitle = models.course?.attributes.title
  let newState = payload.courseDateTimeSetting
  let newEvent: CourseEditBasic = {
    ...createBlankEvent(),
    root_id: payload.planbookId,
    root_type: "planbook",
    date_plan_scheduled_for: null,

    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

    primary_object_type: "course",
    primary_object_id: payload.courseId,
    primary_object_specifier: "enter_times_state",
    primary_object_id_and_ancestor_ids: null,

    adverbial_verb: null,

    adverbial_object_type: null,
    adverbial_object_id: null,
    adverbial_object_specifier: null,
    adverbial_object_id_and_ancestor_ids: null,
  }

  return {
    title: "Change course times state",
    titleForAnalytics: "Course - Set Enter Times State",
    description: `Changed your "${courseTitle}" class to ${stateDescriptions[newState]}`,
    context: defaultCourseContext(payload, models),
    activity: defaultCourseActivity(payload, models),
    newAnalyticsEvents: [newEvent],
  }
}
